/**
 * Query Key Factory for Tenant Queries
 *
 * Centralized query key management following TanStack Query best practices.
 * Prevents typos in query keys and enables easier cache invalidation.
 *
 * @see https://tanstack.com/query/latest/docs/react/guides/important-defaults
 */

export const tenantKeys = {
  /**
   * Base key for all tenant queries
   */
  all: ['tenants'] as const,

  /**
   * Keys for detail queries (single tenant)
   */
  details: () => [...tenantKeys.all, 'detail'] as const,

  /**
   * Key for a specific tenant by ID
   *
   * @param id - Tenant ID (UUID)
   * @returns Query key for the tenant detail query
   *
   * @example
   * ```typescript
   * const queryKey = tenantKeys.detail('123e4567-e89b-12d3-a456-426614174000');
   * // Returns: ['tenants', 'detail', '123e4567-e89b-12d3-a456-426614174000']
   * ```
   */
  detail: (id: string) => [...tenantKeys.details(), id] as const,

  /**
   * Key for tenant settings query
   *
   * @returns Query key for the tenant settings query
   *
   * @example
   * ```typescript
   * const queryKey = tenantKeys.settings();
   * // Returns: ['tenants', 'settings']
   * ```
   */
  settings: () => [...tenantKeys.all, 'settings'] as const,
} as const;
